// Vertical and city filters for the lead list (plan §4.5). In-memory like the other filters,
// and hidden while there is only one of each — the dentist-only UI looks exactly as before.

import { useMemo } from 'react';
import { useNicheContext } from '../../lib/nicheContext.js';
import type { LeadFilterState } from './LeadFilters.js';
import type { LeadListRow } from './useLeads.js';

export interface NicheOption {
  key: string;
  label: string;
}

export interface NicheOptions {
  verticals: NicheOption[];
  cities: NicheOption[];
}

export const NO_NICHE_OPTIONS: NicheOptions = { verticals: [], cities: [] };

export { useNicheContext } from '../../lib/nicheContext.js';

/** Distinct vertical/city keys among the rows and active configuration. */
export function useNicheFilterOptions(rows: readonly LeadListRow[]): NicheOptions {
  const { verticals, cities, activeVerticals, activeCities } = useNicheContext();
  return useMemo(() => {
    const verticalName = new Map(verticals.map((v) => [v.key, v.name_bg]));
    const cityName = new Map(cities.map((c) => [c.key, c.name_bg]));

    const vKeys = new Set([
      ...rows.map((r) => r.vertical_key).filter((k): k is string => typeof k === 'string'),
      ...activeVerticals.map((v) => v.key),
    ]);
    const cKeys = new Set([
      ...rows.map((r) => r.city_key).filter((k): k is string => typeof k === 'string'),
      ...activeCities.map((c) => c.key),
    ]);

    return {
      verticals: [...vKeys].sort().map((key) => ({
        key,
        label: verticalName.get(key) ?? key,
      })),
      cities: [...cKeys].sort().map((key) => ({
        key,
        label: cityName.get(key) ?? key,
      })),
    };
  }, [rows, verticals, cities, activeVerticals, activeCities]);
}

type NicheFilter = Pick<LeadFilterState, 'vertical' | 'city'>;

export function NicheFilterFields({
  options,
  filters,
  onChange,
}: {
  options: NicheOptions;
  filters: NicheFilter;
  onChange: (patch: Partial<NicheFilter>) => void;
}) {
  if (options.verticals.length === 0 && options.cities.length === 0) return null;
  return (
    <div className="lead-filters__niche">
      {options.verticals.length > 0 ? (
        <select
          className="select select--sm"
          value={filters.vertical}
          onChange={(e) => onChange({ vertical: e.target.value })}
          aria-label="Ниша"
        >
          <option value="">Всички ниши</option>
          {options.verticals.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      ) : null}
      {options.cities.length > 0 ? (
        <select
          className="select select--sm"
          value={filters.city}
          onChange={(e) => onChange({ city: e.target.value })}
          aria-label="Град"
        >
          <option value="">Всички градове</option>
          {options.cities.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}
